import * as React from "react"
import { useParams } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"

import { LoadingState } from "@/components/ui/loading-state"
import { Users, Check, X } from "lucide-react"
import { useAuthStore } from "@/stores/auth.store"

// Mock group data for invite
const mockInviteGroup = {
  id: "group1",
  name: "Roommates",
  description: "Shared apartment expenses",
  emoji: "🏠",
  currency: "EUR",
  total_expenses: 850.75,
  memberCount: 3,
  invitedBy: {
    id: "user2",
    name: "Sarah Smith",
    email: "sarah@example.com",
    avatar_url: "",
  },
  recentMembers: [
    {
      id: "user1",
      name: "John Doe",
      email: "john@example.com",
      avatar_url: "",
    },
    {
      id: "user2",
      name: "Sarah Smith",
      email: "sarah@example.com",
      avatar_url: "",
    },
    {
      id: "user3",
      name: "Mike Johnson",
      email: "mike@example.com",
      avatar_url: "",
    },
  ],
}

export const JoinGroupPage: React.FC = () => {
  const { inviteCode } = useParams<{ inviteCode: string }>()
  const { isAuthenticated, user: _user } = useAuthStore()
  const [isLoading, setIsLoading] = React.useState(true)
  const [isJoining, setIsJoining] = React.useState(false)
  const [hasJoined, setHasJoined] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // Simulate loading
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false)
      // Simulate invalid invite code occasionally
      if (inviteCode === "INVALID") {
        setError("This invite link is invalid or has expired.")
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [inviteCode])

  const handleJoinGroup = async () => {
    if (!isAuthenticated) {
      // TODO: Redirect to login with return URL
      return
    }

    setIsJoining(true)
    try {
      // TODO: Implement join group API call
      await new Promise(resolve => setTimeout(resolve, 2000)) // Simulate API call
      setHasJoined(true)
    } catch (error) {
      setError("Failed to join group. Please try again.")
    } finally {
      setIsJoining(false)
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <LoadingState message="Loading group invitation..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-md">
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="mb-4">
              <X className="h-12 w-12 mx-auto text-destructive" />
            </div>
            <h3 className="font-semibold mb-2 text-destructive">Invalid Invitation</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {error}
            </p>
            <Button asChild>
              <a href="/">Go to Dashboard</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (hasJoined) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-md">
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="mb-4">
              <Check className="h-12 w-12 mx-auto text-green-600" />
            </div>
            <h3 className="font-semibold mb-2 text-green-600">Welcome to the group!</h3>
            <p className="text-sm text-muted-foreground mb-4">
              You've successfully joined "{mockInviteGroup.name}". You can now start tracking expenses together.
            </p>
            <Button asChild>
              <a href="/">Go to Dashboard</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-16 max-w-md">
      <Card>
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
              {mockInviteGroup.emoji ? (
                <span className="text-2xl">{mockInviteGroup.emoji}</span>
              ) : (
                <Users className="h-6 w-6 text-primary" />
              )}
            </div>
          </div>
          <CardTitle className="text-xl">You're invited to join</CardTitle>
          <CardDescription className="text-lg font-semibold">
            {mockInviteGroup.name}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Group Info */}
          <div className="space-y-4">
            {mockInviteGroup.description && (
              <p className="text-sm text-muted-foreground text-center">
                {mockInviteGroup.description}
              </p>
            )}

            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold">{mockInviteGroup.memberCount}</div>
                <div className="text-xs text-muted-foreground">Members</div>
              </div>
              <div>
                <div className="text-2xl font-bold">
                  ${mockInviteGroup.total_expenses.toFixed(2)}
                </div>
                <div className="text-xs text-muted-foreground">Total Spent</div>
              </div>
            </div>
          </div>

          {/* Invited By */}
          <div className="text-center">
            <div className="text-sm text-muted-foreground mb-2">Invited by</div>
            <div className="flex items-center justify-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={mockInviteGroup.invitedBy.avatar_url} />
                <AvatarFallback className="text-xs">
                  {mockInviteGroup.invitedBy.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <span className="font-medium">{mockInviteGroup.invitedBy.name}</span>
            </div>
          </div>

          {/* Current Members */}
          <div>
            <div className="text-sm text-muted-foreground mb-2 text-center">Current members</div>
            <div className="flex justify-center -space-x-2">
              {mockInviteGroup.recentMembers.slice(0, 4).map((member) => (
                <Avatar key={member.id} className="h-8 w-8 border-2 border-background">
                  <AvatarImage src={member.avatar_url} />
                  <AvatarFallback className="text-xs">
                    {member.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              ))}
              {mockInviteGroup.memberCount > 4 && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-background bg-muted text-xs font-medium">
                  +{mockInviteGroup.memberCount - 4}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {isAuthenticated ? (
              <Button
                onClick={handleJoinGroup}
                disabled={isJoining}
                className="w-full"
              >
                {isJoining ? "Joining..." : "Join Group"}
              </Button>
            ) : (
              <div className="space-y-2">
                <Button asChild className="w-full">
                  <a href={`/auth/login?redirect=/join/${inviteCode}`}>
                    Sign in to join
                  </a>
                </Button>
                <Button variant="outline" asChild className="w-full">
                  <a href={`/auth/register?redirect=/join/${inviteCode}`}>
                    Create account
                  </a>
                </Button>
              </div>
            )}
            <Button variant="ghost" asChild className="w-full">
              <a href="/">Maybe later</a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}